import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';

/**
 * DebugContext — store global para el Debug Drawer (superadmin only).
 *
 * Características clave:
 * - Persiste cross-route: vive en el provider montado en la raíz, no en
 *   componentes individuales. Cambiar de tab/vista NO borra el log.
 * - Solo se borra con el botón "Limpiar" del drawer.
 * - Cero overhead para no-superadmins: el Provider expone `enabled = false`
 *   y los interceptores chequean ese flag (vía función global) antes de
 *   registrar entradas.
 */

export interface NetworkEntry {
  id: string;
  method: string;
  url: string;
  fullUrl: string;
  status?: number;
  statusText?: string;
  durationMs?: number;
  startedAt: number; // ms epoch
  finishedAt?: number;
  sizeBytes?: number;
  error?: string;
  requestHeaders?: Record<string, any>;
  requestData?: any;
  responseHeaders?: Record<string, any>;
  responseData?: any;
}

export interface StepEntry {
  id: string;
  label: string;
  phase: 'start' | 'end' | 'info';
  durationMs?: number;
  meta?: Record<string, any>;
  at: number; // ms epoch
}

interface DebugContextValue {
  enabled: boolean;
  paused: boolean;
  setPaused: (v: boolean) => void;
  network: NetworkEntry[];
  steps: StepEntry[];
  startedAt: number;
  pushNetworkStart: (entry: Omit<NetworkEntry, 'id' | 'startedAt'> & { id?: string }) => string;
  pushNetworkEnd: (id: string, patch: Partial<NetworkEntry>) => void;
  pushStep: (entry: Omit<StepEntry, 'id' | 'at'>) => void;
  clear: () => void;
}

const DebugContext = createContext<DebugContextValue | undefined>(undefined);

const MAX_ENTRIES = 500;

// Funciones globales para que los interceptores de axios puedan empujar
// datos sin necesidad de hooks de React.
declare global {
  interface Window {
    __debugDrawer?: {
      enabled: () => boolean;
      pushNetworkStart: DebugContextValue['pushNetworkStart'];
      pushNetworkEnd: DebugContextValue['pushNetworkEnd'];
      pushStep: DebugContextValue['pushStep'];
    };
  }
}

interface DebugProviderProps {
  enabled: boolean;
  children: ReactNode;
}

export const DebugProvider: React.FC<DebugProviderProps> = ({ enabled, children }) => {
  const [paused, setPaused] = useState(false);
  const [network, setNetwork] = useState<NetworkEntry[]>([]);
  const [steps, setSteps] = useState<StepEntry[]>([]);
  const startedAtRef = useRef<number>(Date.now());
  const counterRef = useRef(0);
  const pausedRef = useRef(paused);
  const enabledRef = useRef(enabled);

  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  const nextId = useCallback(() => {
    counterRef.current += 1;
    return `${Date.now()}-${counterRef.current}`;
  }, []);

  const pushNetworkStart = useCallback<DebugContextValue['pushNetworkStart']>(
    (entry) => {
      if (!enabledRef.current || pausedRef.current) {
        return entry.id || nextId();
      }
      const id = entry.id || nextId();
      const full: NetworkEntry = {
        id,
        method: entry.method,
        url: entry.url,
        fullUrl: entry.fullUrl,
        startedAt: Date.now(),
        requestHeaders: entry.requestHeaders,
        requestData: entry.requestData,
      };
      setNetwork((prev) => {
        const next = [...prev, full];
        if (next.length > MAX_ENTRIES) next.splice(0, next.length - MAX_ENTRIES);
        return next;
      });
      return id;
    },
    [nextId]
  );

  const pushNetworkEnd = useCallback<DebugContextValue['pushNetworkEnd']>((id, patch) => {
    if (!enabledRef.current) return;
    setNetwork((prev) =>
      prev.map((e) => (e.id === id ? { ...e, ...patch, finishedAt: Date.now() } : e))
    );
  }, []);

  const pushStep = useCallback<DebugContextValue['pushStep']>(
    (entry) => {
      if (!enabledRef.current || pausedRef.current) return;
      const id = nextId();
      const full: StepEntry = { id, at: Date.now(), ...entry };
      setSteps((prev) => {
        const next = [...prev, full];
        if (next.length > MAX_ENTRIES) next.splice(0, next.length - MAX_ENTRIES);
        return next;
      });
    },
    [nextId]
  );

  const clear = useCallback(() => {
    setNetwork([]);
    setSteps([]);
    startedAtRef.current = Date.now();
  }, []);

  // Auto-clear en navegación / reload: el log queda scoped a la sesión de página.
  // - beforeunload: F5, cierre de tab, navegación a URL externa.
  // - popstate: navegación SPA con back/forward o pushState + dispatch popstate
  //   (patrón usado en ClientCard.tsx para cambiar de vista).
  // Switching entre tabs internos del drawer (Network/Steps/Page/Perf) NO
  // dispara estos eventos — eso es UI state puro.
  useEffect(() => {
    if (!enabled) return;
    const onBeforeUnload = () => {
      clear();
    };
    const onPopState = () => {
      clear();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('popstate', onPopState);
    };
  }, [enabled, clear]);

  // Listener para custom events `debug:step` desde cualquier parte del código.
  useEffect(() => {
    if (!enabled) return;
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent).detail;
      if (!detail || typeof detail.label !== 'string') return;
      pushStep({
        label: detail.label,
        phase: detail.phase || 'info',
        durationMs: detail.durationMs,
        meta: detail.meta,
      });
    };
    window.addEventListener('debug:step', handler as EventListener);
    return () => window.removeEventListener('debug:step', handler as EventListener);
  }, [enabled, pushStep]);

  // Exponemos el bridge en window para los interceptors de axios.
  useEffect(() => {
    if (!enabled) {
      delete window.__debugDrawer;
      return;
    }
    window.__debugDrawer = {
      enabled: () => enabledRef.current && !pausedRef.current,
      pushNetworkStart,
      pushNetworkEnd,
      pushStep,
    };
    return () => {
      delete window.__debugDrawer;
    };
  }, [enabled, pushNetworkStart, pushNetworkEnd, pushStep]);

  const value = useMemo<DebugContextValue>(
    () => ({
      enabled,
      paused,
      setPaused,
      network,
      steps,
      startedAt: startedAtRef.current,
      pushNetworkStart,
      pushNetworkEnd,
      pushStep,
      clear,
    }),
    [enabled, paused, network, steps, pushNetworkStart, pushNetworkEnd, pushStep, clear]
  );

  return <DebugContext.Provider value={value}>{children}</DebugContext.Provider>;
};

export const useDebug = (): DebugContextValue => {
  const ctx = useContext(DebugContext);
  if (!ctx) {
    // No estamos dentro del provider (ej: usuario no-superadmin) → no-op stub.
    return {
      enabled: false,
      paused: false,
      setPaused: () => {},
      network: [],
      steps: [],
      startedAt: Date.now(),
      pushNetworkStart: () => '',
      pushNetworkEnd: () => {},
      pushStep: () => {},
      clear: () => {},
    };
  }
  return ctx;
};
